import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Copy, History, KeyRound, Loader2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Pill, type PillTone } from '@/components/ui/pill';
import { RowActions } from '@/components/ui/row-actions';
import { Tag, channelLabel, channelToneFor } from '@/components/ui/tag';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableSkeleton,
} from '@/components/ui/data-table';
import { PlatformIcon } from '@/components/PlatformIcon';
import { useDeleteOutreachAccount, useOutreachAccountAudit } from '@/hooks/useOutreachAccounts';
import { ACCOUNT_STATUS_LABEL, auditActionLabel } from '@/lib/outreachAccounts';
import { formatDateTime } from '@/lib/dates';
import type { OutreachAccount, OutreachAccountStatus } from '@/api/types';
import { AccountFormDialog } from './AccountFormDialog';
import { PasswordCell } from './PasswordCell';

const STATUS_TONE: Record<OutreachAccountStatus, PillTone> = {
  ACTIVE: 'success',
  BANNED: 'rejected',
  RETIRED: 'muted',
};

/**
 * The vault table, shared by an outreacher's profile (no outreacher column)
 * and the all-accounts page. Edit / history / delete live in the row menu;
 * delete asks first, in-page.
 */
export function AccountsTable({
  accounts,
  loading,
  showOutreacher,
  emptyHint,
}: {
  accounts: OutreachAccount[];
  loading?: boolean;
  showOutreacher?: boolean;
  emptyHint: string;
}): JSX.Element {
  const remove = useDeleteOutreachAccount();
  const [editing, setEditing] = useState<OutreachAccount | null>(null);
  const [deleting, setDeleting] = useState<OutreachAccount | null>(null);
  const [history, setHistory] = useState<OutreachAccount | null>(null);

  async function confirmDelete(): Promise<void> {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.publicId);
      toast.success(`Deleted @${deleting.username}`);
    } catch (err) {
      toast.error(`Couldn't delete @${deleting.username}: ${(err as Error).message}`);
    }
  }

  if (!loading && accounts.length === 0) {
    return <EmptyState icon={<KeyRound className="h-6 w-6" />} title="No accounts yet." hint={emptyHint} />;
  }

  const cols = showOutreacher ? 10 : 9;

  return (
    <>
      <DataTable aria-label="Outreach accounts">
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>Account</DataTableHeaderCell>
            {showOutreacher && <DataTableHeaderCell>Outreacher</DataTableHeaderCell>}
            <DataTableHeaderCell>Platform</DataTableHeaderCell>
            <DataTableHeaderCell>Login email</DataTableHeaderCell>
            <DataTableHeaderCell>Password</DataTableHeaderCell>
            <DataTableHeaderCell>Status</DataTableHeaderCell>
            <DataTableHeaderCell>Notes</DataTableHeaderCell>
            <DataTableHeaderCell>Last revealed</DataTableHeaderCell>
            <DataTableHeaderCell narrow>
              <span className="sr-only">Actions</span>
            </DataTableHeaderCell>
          </tr>
        </DataTableHeader>
        {loading ? (
          <DataTableSkeleton rows={4} cols={Array.from({ length: cols - 1 }, () => 'w-20')} />
        ) : (
          <tbody>
            {accounts.map((a) => (
              <DataTableRow key={a.publicId} state={a.status === 'ACTIVE' ? 'default' : 'terminal'}>
                <DataTableCell anchor>
                  <div className="flex items-center gap-1.5">
                    <PlatformIcon platform={a.platform} size={14} className="text-muted-foreground" />
                    <span>@{a.username}</span>
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(a.username);
                        toast.success(`Copied @${a.username}`);
                      }}
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      aria-label={`Copy username @${a.username}`}
                      title="Copy username"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </DataTableCell>
                {showOutreacher && (
                  <DataTableCell data-label="Outreacher">
                    <Link to={`/admin/outreachers/${a.inviter.publicId}`} className="hover:underline">
                      {a.inviter.name}
                    </Link>
                  </DataTableCell>
                )}
                <DataTableCell data-label="Platform">
                  <Tag tone={channelToneFor(a.platform)}>{channelLabel(a.platform)}</Tag>
                </DataTableCell>
                <DataTableCell muted data-label="Login email">
                  {a.email ?? '—'}
                </DataTableCell>
                <DataTableCell data-label="Password">
                  <PasswordCell account={a} />
                </DataTableCell>
                <DataTableCell data-label="Status">
                  <Pill tone={STATUS_TONE[a.status]}>{ACCOUNT_STATUS_LABEL[a.status]}</Pill>
                </DataTableCell>
                <DataTableCell muted data-label="Notes" className="max-w-[14rem] truncate" title={a.notes ?? undefined}>
                  {a.notes ?? '—'}
                </DataTableCell>
                <DataTableCell muted data-label="Last revealed">
                  {a.lastRevealedAt ? formatDateTime(a.lastRevealedAt) : 'Never'}
                </DataTableCell>
                <DataTableCell actions>
                  <RowActions
                    triggerLabel={`Actions for @${a.username}`}
                    items={[
                      { label: 'Edit', icon: Pencil, onClick: () => setEditing(a) },
                      { label: 'Reveal history', icon: History, onClick: () => setHistory(a) },
                      { label: 'Delete', icon: Trash2, destructive: true, onClick: () => setDeleting(a) },
                    ]}
                  />
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>

      <AccountFormDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} account={editing} />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        tone="destructive"
        title={deleting ? `Delete @${deleting.username} on ${channelLabel(deleting.platform)}?` : 'Delete account?'}
        description="This removes the login and its stored password for good. The reveal history is kept."
        confirmLabel="Delete account"
        pending={remove.isPending}
        onConfirm={() => confirmDelete()}
      />
      <AuditHistoryDialog account={history} onClose={() => setHistory(null)} />
    </>
  );
}

function AuditHistoryDialog({ account, onClose }: { account: OutreachAccount | null; onClose: () => void }): JSX.Element {
  const audit = useOutreachAccountAudit(account?.publicId ?? null);
  const events = audit.data?.events ?? [];
  return (
    <Dialog open={account !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{account ? `Reveal history for @${account.username}` : 'Reveal history'}</DialogTitle>
          <DialogDescription>Every time an admin revealed, copied or changed this password.</DialogDescription>
        </DialogHeader>
        {audit.isLoading ? (
          <p className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : audit.error ? (
          <p role="alert" className="text-sm text-destructive">
            Couldn't load the history: {(audit.error as Error).message}
          </p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No one has revealed or changed this password yet.</p>
        ) : (
          <ul className="max-h-80 space-y-2 overflow-y-auto" aria-label="Reveal history">
            {events.map((e) => (
              <li key={e.id} className="rounded-md border p-2.5 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span>
                    <span className="font-medium">{e.actorName ?? e.actorUserId}</span>{' '}
                    <span className="text-muted-foreground">{auditActionLabel(e.action).toLowerCase()}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(e.createdAt)}</span>
                </div>
                {(e.ip || e.userAgent) && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground" title={e.userAgent ?? undefined}>
                    {[e.ip, e.userAgent].filter(Boolean).join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
